/**
 * Procedural planet and moon surface shaders.
 * Uses 3D simplex noise FBM for continents (no UV pole distortion).
 * Color bands from deep ocean to mountain. Fresnel rim glow triggers bloom.
 *
 * Camera looks down Y-axis (top-down), so rim glow uses the XZ normal component.
 */

// ---- 3D Simplex Noise GLSL (Ashima/Stefan Gustavson) ----
// MIT licensed, standard approach for procedural 3D noise in shaders

const NOISE_3D = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float fbm(vec3 p) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 5; i++) {
    value += amplitude * snoise(p);
    p *= 2.0;
    amplitude *= 0.5;
  }
  return value;
}
`;

// ---- Planet Surface Shader ----

export const planetVertexShader = /* glsl */ `
varying vec3 vNormal;
varying vec3 vWorldPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const planetFragmentShader = /* glsl */ `
${NOISE_3D}

uniform float uTime;
uniform float uRadius;

varying vec3 vNormal;
varying vec3 vWorldPosition;

void main() {
  // Use world position for 3D noise sampling (no UV distortion at poles)
  vec3 noisePos = vWorldPosition * 0.03 + uTime * 0.001;
  float terrain = fbm(noisePos);

  // Color bands: deep ocean -> shallow water -> beach -> lowland -> highland -> mountain
  vec3 deepOcean   = vec3(0.05, 0.08, 0.25);
  vec3 shallowWater = vec3(0.1, 0.2, 0.45);
  vec3 beach        = vec3(0.55, 0.5, 0.35);
  vec3 lowland      = vec3(0.15, 0.35, 0.12);
  vec3 highland     = vec3(0.25, 0.22, 0.15);
  vec3 mountain     = vec3(0.6, 0.58, 0.55);

  vec3 color;
  if (terrain < -0.1) {
    color = mix(deepOcean, shallowWater, smoothstep(-0.4, -0.1, terrain));
  } else if (terrain < 0.0) {
    color = mix(shallowWater, beach, smoothstep(-0.1, 0.0, terrain));
  } else if (terrain < 0.15) {
    color = mix(beach, lowland, smoothstep(0.0, 0.15, terrain));
  } else if (terrain < 0.35) {
    color = mix(lowland, highland, smoothstep(0.15, 0.35, terrain));
  } else {
    color = mix(highland, mountain, smoothstep(0.35, 0.6, terrain));
  }

  // Fresnel rim glow — camera looks down Y axis, so rim = horizontal normal component
  float rimFactor = 1.0 - abs(vNormal.y);
  float rim = pow(rimFactor, 3.0);

  // Atmosphere edge color (HDR blue for bloom)
  vec3 atmosphereColor = vec3(0.3, 0.5, 1.5);
  color += atmosphereColor * rim * 0.8;

  // Basic diffuse from above (sun-like)
  float diffuse = max(dot(vNormal, vec3(0.3, 0.8, -0.2)), 0.1);
  color *= diffuse * 1.2;

  // Animated ocean shimmer — subtle wave pattern on water regions
  if (terrain < -0.1) {
    float wave = snoise(vWorldPosition * 0.1 + vec3(uTime * 0.01, uTime * 0.008, 0.0)) * 0.03;
    color += vec3(0.02, 0.06, 0.15) * (1.0 + wave * 4.0);
    // Emissive ocean glow
    color += vec3(0.02, 0.04, 0.12) * 2.0;
  }

  gl_FragColor = vec4(color, 1.0);
}
`;

// ---- Cloud Layer Shader ----

export const cloudVertexShader = /* glsl */ `
varying vec3 vNormal;
varying vec3 vWorldPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const cloudFragmentShader = /* glsl */ `
${NOISE_3D}

uniform float uTime;

varying vec3 vNormal;
varying vec3 vWorldPosition;

void main() {
  // Domain warping: swirl the noise input for organic cloud shapes (cyclones, fronts)
  vec3 warpInput = vWorldPosition * 0.015;
  vec3 warpOffset = vec3(
    snoise(warpInput + vec3(uTime * 0.004, 0.0, 0.0)),
    0.0,
    snoise(warpInput + vec3(50.0, 0.0, uTime * 0.003))
  ) * 10.0;
  vec3 warpedPos = vWorldPosition + warpOffset;

  // Three cloud layers at different scales and drift speeds
  float layer1 = fbm(warpedPos * 0.03 + vec3(uTime * 0.002, 0.0, uTime * 0.001));
  float layer2 = fbm(warpedPos * 0.07 + vec3(uTime * 0.004, 0.0, uTime * 0.003) + 100.0);
  float layer3 = snoise(warpedPos * 0.15 + vec3(uTime * 0.006, 0.0, uTime * 0.005)) * 0.3;

  float coverage = layer1 * 0.5 + layer2 * 0.35 + layer3 * 0.15;
  float cloudAlpha = smoothstep(-0.05, 0.25, coverage);

  // Directional lighting — warm sunlit side, cool shadow
  float sunDot = max(dot(vNormal, normalize(vec3(0.3, 0.8, -0.2))), 0.0);
  vec3 litColor = vec3(1.0, 0.97, 0.9);
  vec3 shadowColor = vec3(0.3, 0.35, 0.55);
  vec3 cloudColor = mix(shadowColor, litColor, smoothstep(0.0, 0.6, sunDot));

  // Edge fade — clouds visible at center, fade at limb
  float rimFactor = 1.0 - abs(vNormal.y);
  float rimFade = 1.0 - pow(rimFactor, 1.8);
  cloudAlpha *= rimFade * 0.7;

  if (cloudAlpha < 0.01) discard;
  gl_FragColor = vec4(cloudColor, cloudAlpha);
}
`;

// ---- Atmosphere Glow Shader (BackSide) ----

export const atmosphereVertexShader = /* glsl */ `
varying vec3 vNormal;

void main() {
  vNormal = normalize(normalMatrix * normal);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const atmosphereFragmentShader = /* glsl */ `
varying vec3 vNormal;

void main() {
  // Multi-layer Fresnel glow — outer blue to inner white
  // BackSide rendering: normals point inward, so dot with view gives rim at edges
  float rimFactor = abs(vNormal.y);
  float outerGlow = pow(1.0 - rimFactor, 2.0);
  float innerGlow = pow(1.0 - rimFactor, 5.0);

  // Color gradient: deep blue at outer edge, brighter blue-white near surface
  vec3 outerColor = vec3(0.15, 0.35, 2.2);
  vec3 innerColor = vec3(0.5, 0.7, 1.8);
  vec3 atmosColor = mix(outerColor, innerColor, innerGlow);

  float glow = outerGlow;
  if (glow < 0.03) discard;
  gl_FragColor = vec4(atmosColor * glow * 1.5, glow * 0.6);
}
`;

// ---- Moon Surface Shader ----

export const moonVertexShader = /* glsl */ `
varying vec3 vNormal;
varying vec3 vLocalPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vLocalPosition = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const moonFragmentShader = /* glsl */ `
${NOISE_3D}

varying vec3 vNormal;
varying vec3 vLocalPosition;

void main() {
  // Low-frequency noise for large crater features (local position = stable as moon orbits)
  float craters = fbm(vLocalPosition * 0.08);
  // High-frequency for surface texture
  float texture = snoise(vLocalPosition * 0.3) * 0.15;

  float terrain = craters + texture;

  // Gray palette with variation
  vec3 darkGray  = vec3(0.35, 0.33, 0.30);
  vec3 lightGray = vec3(0.65, 0.63, 0.60);
  vec3 craterDark = vec3(0.2, 0.18, 0.16);

  vec3 color;
  if (terrain < -0.15) {
    color = mix(craterDark, darkGray, smoothstep(-0.4, -0.15, terrain));
  } else {
    color = mix(darkGray, lightGray, smoothstep(-0.15, 0.3, terrain));
  }

  // Subtle rim lighting
  float rimFactor = 1.0 - abs(vNormal.y);
  float rim = pow(rimFactor, 3.0);
  color += vec3(0.15, 0.15, 0.2) * rim;

  // Diffuse from above
  float diffuse = max(dot(vNormal, vec3(0.3, 0.8, -0.2)), 0.15);
  color *= diffuse * 1.1;

  gl_FragColor = vec4(color, 1.0);
}
`;
